import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  ChevronLeft,
  ChevronRight,
  FolderPlus,
  MoreVertical,
  Trash2,
  RefreshCw,
  Folder,
  FileText,
  Loader2,
  Pencil,
  Download,
  Search,
  Home,
  FolderOpen,
  HardDrive,
  AlertTriangle,
  Upload,
  File,
  FileImage,
  FileSpreadsheet,
  FileCode,
  Sparkles,
} from 'lucide-react';
import { clientsService } from '@/services/api/clients';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type {
  Client,
  Folder as FolderType,
  FolderContents,
  ClientDocument,
  CreateFolderRequest,
  FolderType as FolderTypeEnum,
  FOLDER_TYPE_LABELS,
} from '@/types/client';
import { DOCUMENT_CATEGORY_LABELS } from '@/types/client';
import { DocumentUploadDialog } from '@/components/documents/DocumentUploadDialog';
import { DocumentPreviewDialog } from '@/components/documents/DocumentPreviewDialog';

function truncateFilename(name: string, maxLen = 45): string {
  if (name.length <= maxLen) return name;
  const extIdx = name.lastIndexOf('.');
  const ext = extIdx > 0 ? name.slice(extIdx) : '';
  const base = extIdx > 0 ? name.slice(0, extIdx) : name;
  const keepStart = 20;
  const keepEnd = Math.max(10, maxLen - keepStart - ext.length - 3);
  return base.slice(0, keepStart) + '...' + base.slice(-keepEnd) + ext;
}
import { MetadataExtractionDialog } from '@/components/documents/MetadataExtractionDialog';
import { FileMetadataDialog } from '@/components/documents/FileMetadataDialog';
import { ClientFileChatSidebar } from '@/components/chat/ClientFileChatSidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ClientTimelineTab } from '@/pages/clients/ClientTimelineTab';
import { ClientExtractedEntitiesTab } from '@/pages/clients/ClientExtractedEntitiesTab';
import { ChevronDown } from 'lucide-react';

// File icon helper
function getFileIcon(fileType: string) {
  const type = fileType.toLowerCase();
  if (['pdf'].includes(type)) return <FileText className="h-5 w-5 text-red-500" />;
  if (['doc', 'docx'].includes(type)) return <FileText className="h-5 w-5 text-blue-500" />;
  if (['xls', 'xlsx', 'csv'].includes(type)) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) return <FileImage className="h-5 w-5 text-purple-500" />;
  if (['txt', 'md', 'html'].includes(type)) return <FileCode className="h-5 w-5 text-gray-500" />;
  return <File className="h-5 w-5 text-gray-400" />;
}

export function ClientFileBrowserPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderType | null>(null);
  const [search, setSearch] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<ClientDocument | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataDocument, setMetadataDocument] = useState<ClientDocument | null>(null);
  const [fileMetadataDialogOpen, setFileMetadataDialogOpen] = useState(false);

  // Create folder form
  const [createFolderForm, setCreateFolderForm] = useState<CreateFolderRequest>({
    name: '',
    folderType: 'general',
    clientId: clientId,
    parentFolderId: undefined,
  });

  // Fetch client details
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsService.getClient(clientId!),
    enabled: !!clientId,
  });

  // Fetch folder contents
  const { data: folderContents, isLoading: contentsLoading, refetch } = useQuery({
    queryKey: ['folder-contents', clientId, currentFolderId],
    queryFn: async (): Promise<FolderContents> => {
      if (currentFolderId) {
        return clientsService.getFolderContents(currentFolderId);
      } else {
        // Get client's root-level folders and documents
        return clientsService.getClientFolders(clientId!);
      }
    },
    enabled: !!clientId,
  });

  // Set root folder on initial load
  useEffect(() => {
    if (folderContents?.folder && !currentFolderId) {
      setCurrentFolderId(folderContents.folder.id);
    }
  }, [folderContents, currentFolderId]);

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (input: CreateFolderRequest) => clientsService.createFolder(input),
    onSuccess: () => {
      toast.success('Folder created successfully!');
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      setCreateFolderDialogOpen(false);
      setCreateFolderForm({
        name: '',
        folderType: 'general',
        clientId: clientId,
        parentFolderId: currentFolderId || undefined,
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create folder');
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => clientsService.deleteFolder(folderId),
    onSuccess: () => {
      toast.success('Folder deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['folder-contents'] });
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete folder');
    },
  });

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const navigateToFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
    setSearch('');
  };

  const navigateUp = () => {
    if (folderContents?.folder?.parentFolderId) {
      setCurrentFolderId(folderContents.folder.parentFolderId);
    } else if (folderContents?.breadcrumbs && folderContents.breadcrumbs.length > 1) {
      setCurrentFolderId(folderContents.breadcrumbs[folderContents.breadcrumbs.length - 2].id);
    } else if (currentFolderId) {
      // Go back to root level
      setCurrentFolderId(null);
    }
    setSearch('');
  };

  const openCreateFolderDialog = () => {
    setCreateFolderForm({
      name: '',
      folderType: 'general',
      clientId: clientId,
      parentFolderId: currentFolderId || undefined,
    });
    setCreateFolderDialogOpen(true);
  };

  const getFolderTypeBadge = (type: FolderTypeEnum) => {
    const labels: Record<FolderTypeEnum, string> = {
      client_root: 'Root',
      tax_returns: 'Tax Returns',
      bas: 'BAS',
      financials: 'Financials',
      correspondence: 'Correspondence',
      workpapers: 'Workpapers',
      general: 'General',
    };
    return <Badge variant="outline" className="text-xs">{labels[type] || type}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-gray-500/10 text-gray-500' },
      uploaded: { label: 'Uploaded', className: 'bg-blue-500/10 text-blue-500' },
      indexing: { label: 'Indexing', className: 'bg-amber-500/10 text-amber-500' },
      indexed: { label: 'Indexed', className: 'bg-green-500/10 text-green-500' },
      processing: { label: 'Analyzing', className: 'bg-amber-500/10 text-amber-500' },
      completed: { label: 'Analyzed', className: 'bg-green-500/10 text-green-500' },
      failed: { label: 'Failed', className: 'bg-red-500/10 text-red-500' },
      skipped: { label: 'Skipped', className: 'bg-gray-500/10 text-gray-400' },
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  // Filter items by search
  const filteredSubfolders = (folderContents?.subfolders || []).filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredDocuments = (folderContents?.documents || []).filter(d =>
    d.originalFilename.toLowerCase().includes(search.toLowerCase())
  );

  const isLoading = clientLoading || contentsLoading;

  if (!clientId) {
    return <div>Client ID required</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
      {/* Left Column - Main Content */}
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/panel/files/clients')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {clientLoading ? <Skeleton className="h-8 w-48" /> : client?.displayName}
            </h1>
            <p className="text-sm text-muted-foreground">
              {clientLoading ? <Skeleton className="h-4 w-32 mt-1" /> : client?.clientCode}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMetadataDialogOpen(true)}>
            <Sparkles className="mr-2 h-4 w-4" />
            Index & Analyze
          </Button>
          <Button variant="outline" size="sm" onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          <Button size="sm" onClick={openCreateFolderDialog}>
            <FolderPlus className="mr-2 h-4 w-4" />
            New Folder
          </Button>
        </div>
      </div>

      {/* Client Stats */}
      {client && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">Documents</p>
              <p className="text-sm font-semibold">{client.totalDocuments}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">Indexed</p>
              <p className="text-sm font-semibold text-green-600">{client.indexedDocuments}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">Storage</p>
              <p className="text-sm font-semibold">{formatSize(client.totalSizeBytes)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-semibold capitalize">{client.clientType}</p>
            </CardContent>
          </Card>
        </div>
      )}

        {/* Breadcrumb and Search */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col gap-4">
                {/* Breadcrumb */}
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        className="cursor-pointer flex items-center gap-1"
                        onClick={() => setCurrentFolderId(folderContents?.breadcrumbs?.[0]?.id || null)}
                      >
                        <Home className="h-4 w-4" />
                        {client?.displayName || 'Home'}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {folderContents?.breadcrumbs?.slice(1).map((crumb, index, arr) => (
                      <span key={crumb.id} className="flex items-center">
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                          {index === arr.length - 1 ? (
                            <BreadcrumbPage>{crumb.name}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink
                              className="cursor-pointer"
                              onClick={() => navigateToFolder(crumb.id)}
                            >
                              {crumb.name}
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                      </span>
                    ))}
                  </BreadcrumbList>
                </Breadcrumb>

                {/* Search */}
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search files and folders..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* File Browser */}
          <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <div>
            <CardTitle className="text-base">
              {folderContents?.folder?.name || 'Root'}
            </CardTitle>
            <CardDescription>
              {filteredSubfolders.length} folders, {filteredDocuments.length} files
            </CardDescription>
          </div>
          {folderContents?.folder?.parentFolderId && (
            <Button variant="ghost" size="sm" onClick={navigateUp}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : (filteredSubfolders.length === 0 && filteredDocuments.length === 0) ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">
                {search ? 'No matching items' : 'This folder is empty'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {search
                  ? 'Try a different search term'
                  : 'Create a new folder or upload documents to get started'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Parent Directory (..) - MS-DOS style navigation - always show to go back */}
              <div
                className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => {
                  if (currentFolderId) {
                    navigateUp();
                  } else {
                    // At root level - go back to clients list
                    navigate('/panel/files/clients');
                  }
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Folder className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium">..</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {currentFolderId ? 'Parent Directory' : 'Back to Clients'}
                    </p>
                  </div>
                </div>
                <ChevronLeft className="h-4 w-4 text-muted-foreground" />
              </div>

              {/* Folders */}
              {filteredSubfolders.map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigateToFolder(folder.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{folder.name}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span>{folder.documentCount} files</span>
                        <span>{formatSize(folder.totalSizeBytes)}</span>
                        {getFolderTypeBadge(folder.folderType)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Implement rename
                        }}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFolderToDelete(folder);
                            setDeleteFolderDialogOpen(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}

              {/* Documents */}
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => {
                    setPreviewDocument(doc);
                    setPreviewDialogOpen(true);
                  }}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getFileIcon(doc.fileType)}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate" title={doc.originalFilename}>{truncateFilename(doc.originalFilename)}</p>
                        {doc.documentCategory && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {DOCUMENT_CATEGORY_LABELS[doc.documentCategory] || doc.documentCategory}
                          </Badge>
                        )}
                        {doc.financialYear && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            FY{doc.financialYear}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span>{formatSize(doc.fileSizeBytes)}</span>
                        <span>{formatDate(doc.createdAt)}</span>
                        {getStatusBadge(doc.bedrockStatus)}
                        {doc.aiSummary && (
                          <span className="text-blue-500 truncate max-w-[200px]" title={doc.aiSummary}>
                            {doc.aiSummary}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setPreviewDocument(doc);
                          setPreviewDialogOpen(true);
                        }}>
                          <FileText className="mr-2 h-4 w-4" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setMetadataDocument(doc);
                          setFileMetadataDialogOpen(true);
                        }}>
                          <Sparkles className="mr-2 h-4 w-4" />
                          File Metadata
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          toast.success(`Downloading ${doc.originalFilename}`);
                        }}>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Implement delete
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extracted Information — structured data auto-pulled from docs */}
      {clientId && (
        <Collapsible defaultOpen={false} className="group">
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Extracted Information</CardTitle>
                    <CardDescription>ABN, ACN, directors, addresses, bank details and more, auto-extracted from the client's documents</CardDescription>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <ClientExtractedEntitiesTab clientId={clientId} />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Activity Timeline */}
      {clientId && (
        <Collapsible defaultOpen={false} className="group">
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Activity Timeline</CardTitle>
                    <CardDescription>Documents, emails, notes and deadlines for this client</CardDescription>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <ClientTimelineTab clientId={clientId} />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
      </div>

      {/* Right Column - Chat Sidebar */}
      {client && (
        <div className="lg:sticky lg:top-6 lg:self-start h-[calc(100vh-6rem)]">
          <ClientFileChatSidebar
            clientId={clientId}
            clientName={client.displayName}
          />
        </div>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialogOpen} onOpenChange={setCreateFolderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Create a new folder to organize documents.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folderName">Folder Name *</Label>
              <Input
                id="folderName"
                placeholder="e.g., Tax Returns 2024"
                value={createFolderForm.name}
                onChange={(e) => setCreateFolderForm({ ...createFolderForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="folderType">Folder Type</Label>
              <Select
                value={createFolderForm.folderType}
                onValueChange={(v) => setCreateFolderForm({ ...createFolderForm, folderType: v as FolderTypeEnum })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="tax_returns">Tax Returns</SelectItem>
                  <SelectItem value="bas">BAS</SelectItem>
                  <SelectItem value="financials">Financials</SelectItem>
                  <SelectItem value="correspondence">Correspondence</SelectItem>
                  <SelectItem value="workpapers">Workpapers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createFolderMutation.mutate(createFolderForm)}
              disabled={!createFolderForm.name || createFolderMutation.isPending}
            >
              {createFolderMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog open={deleteFolderDialogOpen} onOpenChange={setDeleteFolderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Folder
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{folderToDelete?.name}</strong>?
              This will also delete all subfolders and documents inside. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => folderToDelete && deleteFolderMutation.mutate(folderToDelete.id)}
              disabled={deleteFolderMutation.isPending}
            >
              {deleteFolderMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        clientId={clientId!}
        clientName={client?.displayName || 'Unknown Client'}
        folderId={currentFolderId || undefined}
        folderPath={folderContents?.folder?.fullPath}
        onUploadComplete={() => {
          refetch();
          toast.success('Documents uploaded successfully');
        }}
      />

      {/* Preview Dialog */}
      <DocumentPreviewDialog
        document={previewDocument}
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
      />

      {/* File Metadata Dialog */}
      <FileMetadataDialog
        document={metadataDocument}
        open={fileMetadataDialogOpen}
        onOpenChange={setFileMetadataDialogOpen}
      />

      {/* Metadata Extraction Dialog */}
      <MetadataExtractionDialog
        open={metadataDialogOpen}
        onOpenChange={setMetadataDialogOpen}
        clientId={clientId!}
        clientName={client?.displayName || 'Unknown Client'}
        onProcessingComplete={() => refetch()}
      />
    </div>
  );
}
